import Big from 'big.js';
import { formatProposalDate, formatGbpAmount, toNumberOrNull } from '@/utils/proposalDocument';
import {
	CLIENT_DOCUMENT_BLUE,
	CLIENT_DOCUMENT_BODY_COLOR,
	CLIENT_DOCUMENT_BORDER,
	CLIENT_DOCUMENT_FONT_FAMILY,
	CLIENT_DOCUMENT_LABEL_COLOR,
	DEFAULT_CLIENT_DOCUMENT_COMPANY,
	buildClientDocumentFooterText,
	buildClientDocumentPageHeaderText,
	clone,
	createSafePdfFilename,
	escapeHtml,
	normalizeClientDocumentCompany,
	normalizeStringArray,
	normalizeRowFormat,
	normalizeText,
	renderClientDocumentFooterText,
	rowFormatClassNames,
} from '@/utils/clientDocumentTheme';

export const ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE = 'assignment-confirmation';
export const ASSIGNMENT_CONFIRMATION_CURRENCY = 'GBP';
export const ASSIGNMENT_CONFIRMATION_TEMPLATE = 'assignment-confirmation-v1';
export const ASSIGNMENT_CONFIRMATION_SOURCE_TEMPLATE =
	'Becketts House - ACO _0250915_BHI Olha Kvastnytsia.pdf';
export const DEFAULT_ASSIGNMENT_CONFIRMATION_SUBTITLE =
	'Ongoing Services - Regular Communal Area Cleaning';
export const DEFAULT_ASSIGNMENT_CONFIRMATION_SERVICE_AREAS = Object.freeze([
	'Carpeted communal halls (10 floors), including internal and fire doors',
	'Two lifts',
	'Decorative elements - pictures',
	'Two carpeted fire-escape staircases',
	'Main entrance with full-height glass window and door',
	'Post lobby',
	'Reception',
	'Communal toilet',
	'Two rear entrances',
	'Porch area',
]);
export const DEFAULT_ASSIGNMENT_CONFIRMATION_REGULAR_TASKS = Object.freeze([
	'Vacuuming all carpeted areas',
	'Sweeping, vacuuming and mopping hard floor areas',
	'Dusting and wiping surfaces, fixtures, skirting boards and windowsills',
	'Wiping internal communal doors',
	'Wiping main and rear entrance doors',
	'Removing marks and fingerprints from all glass elements, including main entrance and rear doors',
	'Disinfecting high-touch surfaces (door handles, lift buttons, handrails)',
	'Lift cleaning (doors, walls, floors, mirrors, control panels)',
	'Cleaning of all staircases, landings and handrails',
	'Wiping post boxes',
	'Wiping reception furniture',
	'Dusting decorative elements - pictures',
	'Cleaning and disinfecting communal toilet',
	'Replacing bin liners in all accessible bins',
	'Sweeping porch area',
	'Emptying ash bins',
]);
export const DEFAULT_ASSIGNMENT_CONFIRMATION_MONTHLY_TASKS = Object.freeze([
	'Cobweb removal in communal areas*',
	'Dusting light fixtures*',
	'Cleaning internal and external glass doors at the main and rear entrances*',
]);
export const DEFAULT_ASSIGNMENT_CONFIRMATION_PRICING_NOTES = Object.freeze([
	'Services are delivered within the agreed weekly hours.',
	'Fees are subject to periodic review.',
]);

const normalizeNumberInput = (value, fallback = '') => {
	if (value === null || value === undefined || value === '') return fallback;
	const numeric = toNumberOrNull(value);
	return numeric === null ? fallback : numeric;
};

const moneyBig = (value) => {
	const numeric = toNumberOrNull(value);
	return numeric === null ? new Big(0) : new Big(numeric);
};

const roundMoney = (value) => Number(value.round(2, Big.roundHalfUp).toFixed(2));

const resolveOverride = (overrideValue, calculatedValue) => {
	const numeric = toNumberOrNull(overrideValue);
	return numeric === null ? calculatedValue : new Big(numeric);
};

const normalizeScheduleRow = (value = {}) => ({
	label: normalizeText(value.label),
	value: normalizeText(value.value),
	...normalizeRowFormat(value),
});

const normalizeSignature = (value = {}, index = -1) => {
	const label = normalizeText(value.label);
	const role = normalizeText(value.role);
	const isLegacySubcontractorRole =
		index === 0 &&
		label.toLocaleLowerCase() === 'subcontractor' &&
		role.toLocaleLowerCase() === 'name';

	return {
		label,
		name: normalizeText(value.name),
		role: isLegacySubcontractorRole ? '' : role,
		mobile: normalizeText(value.mobile),
		signatureLabel: normalizeText(value.signatureLabel, 'Signature'),
		dateLabel: normalizeText(value.dateLabel, 'Date'),
	};
};

export const createAssignmentConfirmationTemplateContent = () => ({
	version: 1,
	documentType: ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE,
	currency: ASSIGNMENT_CONFIRMATION_CURRENCY,
	company: { ...DEFAULT_CLIENT_DOCUMENT_COMPANY },
	footerText: buildClientDocumentFooterText(DEFAULT_CLIENT_DOCUMENT_COMPANY),
	title: 'ASSIGNMENT CONFIRMATION',
	subtitle: DEFAULT_ASSIGNMENT_CONFIRMATION_SUBTITLE,
	reference: 'ACO/20250915/BHI',
	commenceDate: '',
	subcontractorId: null,
	subcontractorName: '',
	subcontractorMobile: '',
	serviceId: null,
	serviceType: '',
	propertyId: null,
	propertyName: '',
	propertyAddress: '',
	scope: {
		title: 'Scope of Work',
		serviceAreas: [...DEFAULT_ASSIGNMENT_CONFIRMATION_SERVICE_AREAS],
		regularTasks: [...DEFAULT_ASSIGNMENT_CONFIRMATION_REGULAR_TASKS],
		monthlyTasksEnabled: true,
		monthlyTasks: [...DEFAULT_ASSIGNMENT_CONFIRMATION_MONTHLY_TASKS],
		monthlyFootnote: '* Safe reachable height only, without specialist equipment.',
	},
	schedule: {
		title: 'Cleaning Schedule',
		rows: [
			{ label: 'Frequency', value: '2 times per week' },
			{ label: 'Suggested Service Days', value: 'Tuesday and Friday' },
			{ label: 'Estimated Hours per Visit', value: '7.5 hours' },
			{ label: 'Time', value: 'Daytime cleaning' },
			{ label: 'Total Weekly Hours', value: '15 hours' },
		],
	},
	pricing: {
		title: 'Pricing',
		hourlyRate: 13.5,
		hoursPerVisit: 7.5,
		visitsPerWeek: 2,
		overrides: {
			feePerVisit: '',
			feePerVisitText: '',
			weeklyCost: '',
		},
		notes: [...DEFAULT_ASSIGNMENT_CONFIRMATION_PRICING_NOTES],
	},
	siteInformation: {
		title: 'Site Information',
		paragraphs: [
			'Cleaning cupboard is located on the ground floor on the left from the lifts.',
			'Access arrangements, including any key, fob or lock-box details, will be confirmed separately before the assignment commences and may change from time to time for security purposes. Cleaning equipment and materials are provided on site by End-Customer.',
		],
	},
	terms: {
		title: 'Terms',
		paragraphs: [
			'This Assignment is governed by the Self-Employed Cleaner Agreement between the Subcontractor and New Order Clean Ltd. Either party may end this Assignment with immediate notice. The Subcontractor may arrange a substitute or share the assignment in accordance with the Self-Employed Cleaner Agreement. Service days may be varied by the Subcontractor with prior notification to the Company. Fee is payable upon submission of a valid invoice.',
		],
	},
	contact: {
		title: 'Contact',
		paragraphs: [
			'Zoriana Kneller, Director - New Order Clean Ltd',
			'Tel: 07553 702075 (WhatsApp) | Email: zoriana@neworderclean.co.uk',
		],
	},
	acceptance: {
		title: 'Acceptance',
		intro: 'By signing below, the Subcontractor confirms:',
		checklist: [
			'Acceptance of this Assignment on the terms above',
			'Receipt of access details and agreement to keep them secure and confidential',
			'Understanding of the scope of work and fee agreed',
			"Will perform services in accordance with the Company's Cleaning Standards Policy",
			'This Assignment is subject to the Self-Employed Cleaner Agreement',
		],
		signatories: [
			{
				label: 'Subcontractor',
				name: '',
				role: '',
				mobile: '',
				signatureLabel: 'Signature',
				dateLabel: 'Date',
			},
			{
				label: 'Company',
				name: 'New Order Clean Ltd',
				role: 'Zoriana Kneller, Director',
				mobile: '07553 702075',
				signatureLabel: 'Signature',
				dateLabel: 'Date',
			},
		],
	},
});

export const normalizeAssignmentConfirmationContent = (raw = {}) => {
	const template = createAssignmentConfirmationTemplateContent();
	const source = raw && typeof raw === 'object' ? raw : {};
	const company = normalizeClientDocumentCompany(source.company || template.company);
	const scope = source.scope || {};
	const schedule = source.schedule || {};
	const pricing = source.pricing || {};
	const siteInformation = source.siteInformation || {};
	const terms = source.terms || {};
	const contact = source.contact || {};
	const acceptance = source.acceptance || {};

	return {
		...clone(template),
		...source,
		version: 1,
		documentType: ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE,
		currency: normalizeText(source.currency, ASSIGNMENT_CONFIRMATION_CURRENCY),
		company,
		footerText: normalizeText(source.footerText, buildClientDocumentFooterText(company)),
		title: normalizeText(source.title, template.title),
		subtitle: normalizeText(source.subtitle, template.subtitle),
		reference: normalizeText(source.reference, template.reference),
		commenceDate: normalizeText(source.commenceDate, template.commenceDate),
		subcontractorId: source.subcontractorId ?? null,
		subcontractorName: normalizeText(source.subcontractorName, template.subcontractorName),
		subcontractorMobile: normalizeText(source.subcontractorMobile, template.subcontractorMobile),
		serviceId: source.serviceId ?? null,
		serviceType: normalizeText(source.serviceType, template.serviceType),
		propertyId: source.propertyId ?? null,
		propertyName: normalizeText(source.propertyName, template.propertyName),
		propertyAddress: normalizeText(source.propertyAddress, template.propertyAddress),
		scope: {
			title: normalizeText(scope.title, template.scope.title),
			serviceAreas: normalizeStringArray(scope.serviceAreas, template.scope.serviceAreas),
			regularTasks: normalizeStringArray(scope.regularTasks, template.scope.regularTasks),
			monthlyTasksEnabled:
				typeof scope.monthlyTasksEnabled === 'boolean'
					? scope.monthlyTasksEnabled
					: template.scope.monthlyTasksEnabled,
			monthlyTasks: normalizeStringArray(scope.monthlyTasks, template.scope.monthlyTasks),
			monthlyFootnote: normalizeText(scope.monthlyFootnote, template.scope.monthlyFootnote),
		},
		schedule: {
			title: normalizeText(schedule.title, template.schedule.title),
			rows: Array.isArray(schedule.rows)
				? schedule.rows.map(normalizeScheduleRow)
				: clone(template.schedule.rows),
		},
		pricing: {
			title: normalizeText(pricing.title, template.pricing.title),
			hourlyRate: normalizeNumberInput(pricing.hourlyRate, template.pricing.hourlyRate),
			hoursPerVisit: normalizeNumberInput(pricing.hoursPerVisit, template.pricing.hoursPerVisit),
			visitsPerWeek: normalizeNumberInput(pricing.visitsPerWeek, template.pricing.visitsPerWeek),
			overrides: {
				feePerVisit: pricing.overrides?.feePerVisit ?? '',
				feePerVisitText: normalizeText(pricing.overrides?.feePerVisitText),
				weeklyCost: pricing.overrides?.weeklyCost ?? '',
			},
			notes: normalizeStringArray(pricing.notes, template.pricing.notes),
		},
		siteInformation: {
			title: normalizeText(siteInformation.title, template.siteInformation.title),
			paragraphs: normalizeStringArray(siteInformation.paragraphs, template.siteInformation.paragraphs),
		},
		terms: {
			title: normalizeText(terms.title, template.terms.title),
			paragraphs: normalizeStringArray(terms.paragraphs, template.terms.paragraphs),
		},
		contact: {
			title: normalizeText(contact.title, template.contact.title),
			paragraphs: normalizeStringArray(contact.paragraphs, template.contact.paragraphs),
		},
		acceptance: {
			title: normalizeText(acceptance.title, template.acceptance.title),
			intro: normalizeText(acceptance.intro, template.acceptance.intro),
			checklist: normalizeStringArray(acceptance.checklist, template.acceptance.checklist),
			signatories: Array.isArray(acceptance.signatories)
				? acceptance.signatories.map(normalizeSignature)
				: clone(template.acceptance.signatories),
		},
	};
};

export const calculateAssignmentConfirmationPricing = (pricingInput = {}) => {
	const pricing = normalizeAssignmentConfirmationContent({ pricing: pricingInput }).pricing;
	const hourlyRate = moneyBig(pricing.hourlyRate);
	const hoursPerVisit = moneyBig(pricing.hoursPerVisit);
	const visitsPerWeek = moneyBig(pricing.visitsPerWeek);
	const calculatedFeePerVisit = hourlyRate.times(hoursPerVisit);
	const feePerVisit = resolveOverride(pricing.overrides.feePerVisit, calculatedFeePerVisit);
	const calculatedWeeklyCost = feePerVisit.times(visitsPerWeek);
	const weeklyCost = resolveOverride(pricing.overrides.weeklyCost, calculatedWeeklyCost);

	return {
		hourlyRate: roundMoney(hourlyRate),
		hoursPerVisit: Number(hoursPerVisit),
		visitsPerWeek: Number(visitsPerWeek),
		feePerVisit: roundMoney(feePerVisit),
		weeklyCost: roundMoney(weeklyCost),
		totalWeeklyHours: roundMoney(hoursPerVisit.times(visitsPerWeek)),
		overridden: {
			feePerVisit: toNumberOrNull(pricing.overrides.feePerVisit) !== null,
			weeklyCost: toNumberOrNull(pricing.overrides.weeklyCost) !== null,
		},
	};
};

const LEGACY_ASSIGNMENT_REFERENCE_PATTERN = /^ACO(?:[\s/_-]|$)/i;

export const isAssignmentConfirmationDocument = (document = {}) => {
	if (
		document?.templateKey === ASSIGNMENT_CONFIRMATION_TEMPLATE ||
		document?.documentType === ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE ||
		document?.content?.documentType === ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE
	) {
		return true;
	}

	const category = normalizeText(document?.category).toLocaleLowerCase();
	const title = normalizeText(document?.title);
	return (
		category === 'assignments' &&
		(title.toLocaleLowerCase() === 'assignment confirmation' ||
			LEGACY_ASSIGNMENT_REFERENCE_PATTERN.test(title))
	);
};

export const getAssignmentConfirmationTableSummary = (document = {}) => {
	if (!isAssignmentConfirmationDocument(document)) return null;
	const content =
		document?.content && typeof document.content === 'object' && !Array.isArray(document.content)
			? document.content
			: {};
	const explicitAmount = toNumberOrNull(
		document?.totalAmount ??
			document?.amount ??
			document?.metadata?.totalAmount ??
			document?.metadata?.weeklyCost,
	);
	const pricing =
		content.pricing && typeof content.pricing === 'object' && !Array.isArray(content.pricing)
			? content.pricing
			: null;
	const hasWeeklyCostOverride = toNumberOrNull(pricing?.overrides?.weeklyCost) !== null;
	const hasVisitsPerWeek = toNumberOrNull(pricing?.visitsPerWeek) !== null;
	const hasFeePerVisitOverride = toNumberOrNull(pricing?.overrides?.feePerVisit) !== null;
	const hasCalculatedFeeInputs =
		toNumberOrNull(pricing?.hourlyRate) !== null &&
		toNumberOrNull(pricing?.hoursPerVisit) !== null;
	const canCalculateAmount =
		hasWeeklyCostOverride ||
		(hasVisitsPerWeek && (hasFeePerVisitOverride || hasCalculatedFeeInputs));
	const amount =
		explicitAmount !== null
			? explicitAmount
			: canCalculateAmount
				? calculateAssignmentConfirmationPricing(pricing).weeklyCost
				: null;

	return {
		reference: normalizeText(
			content.reference ||
				document?.reference ||
				(LEGACY_ASSIGNMENT_REFERENCE_PATTERN.test(normalizeText(document?.title))
					? document.title
					: ''),
		),
		counterparty: normalizeText(
			content.subcontractorName ||
				document?.counterparty ||
				document?.clientName ||
				document?.client?.name ||
				document?.supplierNames?.[0] ||
				document?.metadata?.counterparty,
		),
		siteAddress: normalizeText(
			content.propertyAddress ||
				document?.siteAddress ||
				document?.metadata?.siteAddress ||
				document?.propertySummary ||
				document?.description,
		),
		amount,
	};
};

export const getAssignmentConfirmationPropertySummary = (contentInput = {}) => {
	const content = normalizeAssignmentConfirmationContent(contentInput);
	return [content.propertyName, content.propertyAddress].filter(Boolean).join(', ');
};

export const buildAssignmentConfirmationPayload = (contentInput = {}, extra = {}) => {
	const content = normalizeAssignmentConfirmationContent(contentInput);
	const totals = calculateAssignmentConfirmationPricing(content.pricing);
	const { metadata = {}, content: _ignoredContent, ...rest } = extra || {};
	return {
		...rest,
		documentType: ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE,
		title: content.title || 'Assignment Confirmation',
		reference: content.reference || '',
		documentDate: content.commenceDate || null,
		client: content.subcontractorId || rest.client || null,
		clientName: content.subcontractorName || '',
		propertySummary: getAssignmentConfirmationPropertySummary(content),
		totalAmount: totals.weeklyCost,
		content,
		metadata: {
			template: ASSIGNMENT_CONFIRMATION_TEMPLATE,
			...metadata,
		},
	};
};

export const deriveAssignmentConfirmationFilename = (document = {}) => {
	const content = normalizeAssignmentConfirmationContent(document.content || document);
	return createSafePdfFilename(content.reference || document.reference, 'assignment-confirmation');
};

export const deriveAssignmentConfirmationDownloadFilename = (
	document = {},
	{ supplierName = '', signed = false } = {},
) => {
	const summary = getAssignmentConfirmationTableSummary(document);
	const sanitizePart = (value) =>
		String(value || '')
			.replace(/[<>:"/\\|?*\u0000-\u001f\u007f]/g, '-')
			.replace(/\s+/g, ' ')
			.replace(/^[.\s]+|[.\s]+$/g, '');
	const parts = [
		sanitizePart(summary?.reference) || 'Assignment Confirmation',
		sanitizePart(supplierName || summary?.counterparty),
	].filter(Boolean);
	return `${parts.join(' ')}${signed ? ' signed' : ''}.pdf`;
};

const renderParagraphs = (paragraphs = []) =>
	paragraphs
		.filter((paragraph) => String(paragraph || '').trim())
		.map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
		.join('');

const renderList = (items = [], className = '') => {
	const listItems = items
		.filter((item) => String(item || '').trim())
		.map((item) => `<li>${escapeHtml(item)}</li>`)
		.join('');
	const classAttribute = className ? ` class="${escapeHtml(className)}"` : '';
	return listItems ? `<ul${classAttribute}>${listItems}</ul>` : '';
};

const renderNumberedList = (items = []) => {
	const listItems = items
		.filter((item) => String(item || '').trim())
		.map((item) => `<li>${escapeHtml(item)}</li>`)
		.join('');
	return listItems ? `<ol>${listItems}</ol>` : '';
};

const renderInfoRows = (rows = []) =>
	rows
		.map(
			([label, value]) => `
				<tr>
					<th>${escapeHtml(label)}</th>
					<td>${value || '&nbsp;'}</td>
				</tr>
			`,
		)
		.join('');

const renderScheduleRows = (rows = []) =>
	rows
		.map((row) => {
			const classes = rowFormatClassNames(row, 'assignment-schedule-row');
			return `
				<tr${classes.length ? ` class="${classes.join(' ')}"` : ''}>
					<th>${escapeHtml(row.label)}</th>
					<td>${escapeHtml(row.value)}</td>
				</tr>
			`;
		})
		.join('');

const renderPricingRows = (content, totals) => {
	const generatedFeePerVisitText = `${formatGbpAmount(totals.feePerVisit)} (based on ${totals.hoursPerVisit} hours at ${formatGbpAmount(totals.hourlyRate)}/hr)`;
	const feePerVisitText =
		normalizeText(content?.pricing?.overrides?.feePerVisitText) || generatedFeePerVisitText;
	return renderInfoRows([
		['Fee per Visit', escapeHtml(feePerVisitText)],
		['Weekly Cost', escapeHtml(formatGbpAmount(totals.weeklyCost))],
	]);
};

const renderSignatories = (signatories = []) =>
	signatories
		.map(
			(signatory) => `
				<div class="assignment-signature-box">
					<h3>${escapeHtml(signatory.label)}</h3>
					<strong>${escapeHtml(signatory.name)}</strong>
					${signatory.role ? `<span>${escapeHtml(signatory.role).replace(/\n/g, '<br>')}</span>` : '<span>&nbsp;</span>'}
					${signatory.mobile ? `<span>${escapeHtml(signatory.mobile)}</span>` : '<span>&nbsp;</span>'}
					<div class="assignment-signature-line"></div>
					<small>${escapeHtml(signatory.signatureLabel)}</small>
					<div class="assignment-date-line"></div>
					<small>${escapeHtml(signatory.dateLabel)}</small>
				</div>
			`,
		)
		.join('');

export const generateAssignmentConfirmationHtml = ({ content: contentInput, logoUrl = '/logo-m.svg' } = {}) => {
	const content = normalizeAssignmentConfirmationContent(contentInput);
	const totals = calculateAssignmentConfirmationPricing(content.pricing);
	const footerText = renderClientDocumentFooterText(content.footerText);
	const formattedDate = formatProposalDate(content.commenceDate) || content.commenceDate;
	const scheduleRows = renderScheduleRows(content.schedule.rows);
	const pricingRows = renderPricingRows(content, totals);
	const signatories = renderSignatories(content.acceptance.signatories);
	const infoRows = renderInfoRows([
		['Reference', `<strong>${escapeHtml(content.reference)}</strong>`],
		['Commence Date', escapeHtml(formattedDate)],
		['Subcontractor', `<strong>${escapeHtml(content.subcontractorName)}</strong>${content.subcontractorMobile ? `<br>${escapeHtml(content.subcontractorMobile)}` : ''}`],
		['Property', `<strong>${escapeHtml(content.propertyName)}</strong>${content.propertyAddress ? `<br>${escapeHtml(content.propertyAddress)}` : ''}`],
		['Service', escapeHtml(content.serviceType)],
	]);

	const coverBlock = {
		html: `
			<header class="assignment-topbar">
				${logoUrl ? `<img class="assignment-logo" src="${escapeHtml(logoUrl)}" alt="">` : ''}
			</header>

			<h1>${escapeHtml(content.title)}</h1>
			<p class="assignment-subtitle">${escapeHtml(content.subtitle)}</p>

			<table class="assignment-details-table assignment-info-table">
				<tbody>${infoRows}</tbody>
			</table>`,
	};
	const schedulePricingBlock = {
		html: `
			<section class="assignment-section assignment-grid-section">
				<div>
					<h2>${escapeHtml(content.schedule.title)}</h2>
					<table class="assignment-details-table">
						<tbody>${scheduleRows}</tbody>
					</table>
				</div>
				<div>
					<h2>${escapeHtml(content.pricing.title)}</h2>
					<table class="assignment-details-table">
						<tbody>${pricingRows}</tbody>
					</table>
					<div class="assignment-notes">${renderParagraphs(content.pricing.notes)}</div>
				</div>
			</section>`,
	};
	const siteInformationBlock = {
		html: `
			<section class="assignment-section">
				<h2>${escapeHtml(content.siteInformation.title)}</h2>
				${renderParagraphs(content.siteInformation.paragraphs)}
			</section>`,
	};
	const serviceAreasBlock = {
		splitList: true,
		html: `
			<section class="assignment-section assignment-scope-section">
				<p class="assignment-section-kicker">${escapeHtml(content.scope.title)}</p>
				<h2>Service Areas</h2>
				${renderList(content.scope.serviceAreas, 'assignment-compact-list')}
			</section>`,
	};
	const regularTasksBlock = {
		splitList: true,
		html: `
			<section class="assignment-section assignment-scope-section">
				<h2>Regular Cleaning Tasks</h2>
				${renderList(content.scope.regularTasks, 'assignment-compact-list assignment-task-list')}
			</section>`,
	};
	const monthlyTasksBlock = content.scope.monthlyTasksEnabled
		? {
			html: `
				<section class="assignment-section assignment-scope-section">
					<h2>Monthly Tasks</h2>
					${renderList(content.scope.monthlyTasks)}
					${content.scope.monthlyFootnote ? `<p class="assignment-footnote">${escapeHtml(content.scope.monthlyFootnote)}</p>` : ''}
				</section>`,
		}
		: null;
	const termsBlock = {
		html: `
			<section class="assignment-section">
				<h2>${escapeHtml(content.terms.title)}</h2>
				${renderParagraphs(content.terms.paragraphs)}
			</section>`,
	};
	const contactBlock = {
		html: `
			<section class="assignment-section">
				<h2>${escapeHtml(content.contact.title)}</h2>
				${renderParagraphs(content.contact.paragraphs)}
			</section>`,
	};
	const acceptanceBlock = {
		html: `
			<section class="assignment-section assignment-acceptance-section">
				<h2>${escapeHtml(content.acceptance.title)}</h2>
				<p>${escapeHtml(content.acceptance.intro)}</p>
				${renderNumberedList(content.acceptance.checklist)}
				<div class="assignment-signatures">${signatories}</div>
			</section>`,
	};
	const blocks = [
		coverBlock,
		schedulePricingBlock,
		serviceAreasBlock,
		regularTasksBlock,
		monthlyTasksBlock,
		siteInformationBlock,
		termsBlock,
		contactBlock,
		acceptanceBlock,
	].filter(Boolean);
	const flowBlocksHtml = blocks
		.map(
			(block) =>
				`<div class="assignment-flow-block"${block.splitList ? ' data-split-list="true"' : ''}>${block.html}</div>`,
		)
		.join('');

	return `
		<div class="pdf-gen-root pdf-gen-invoice-document assignment-pdf-root" data-client-document-autopaginate="assignment-confirmation">
			<div class="assignment-document assignment-pagination-source" aria-hidden="true">
${flowBlocksHtml}
			</div>
			<template data-assignment-footer-template>${footerText}</template>
			<template data-assignment-header-template>${escapeHtml(buildClientDocumentPageHeaderText(content.reference, content.title))}</template>
		</div>
		<style>
			.assignment-pdf-root {
				width: 100%;
				background: #f5f5f5;
				padding: 0;
				font-family: ${CLIENT_DOCUMENT_FONT_FAMILY};
				color: ${CLIENT_DOCUMENT_BODY_COLOR};
			}

			.assignment-document {
				position: relative;
				width: 8.27in;
				height: 11.68in;
				margin: 0 auto;
				padding: 0.3in 0.74in 1.02in;
				background: #fff;
				box-sizing: border-box;
				font-size: 12px;
				font-weight: 400;
				line-height: 1.38;
				overflow: hidden;
				page-break-after: always;
				break-after: page;
			}

			.assignment-document--last {
				page-break-after: auto;
				break-after: auto;
			}

			.assignment-pagination-source {
				position: absolute;
				top: 0;
				left: -10000px;
				height: auto;
				min-height: 0;
				overflow: visible;
				visibility: hidden;
				pointer-events: none;
				page-break-after: auto;
				break-after: auto;
			}

			.assignment-flow-block {
				display: flow-root;
			}

			.assignment-topbar {
				display: flex;
				justify-content: flex-end;
				align-items: flex-start;
				min-height: 82px;
			}

			.assignment-logo {
				width: auto;
				max-width: 96px;
				max-height: 75px;
				height: auto;
				object-fit: contain;
			}

			.assignment-document h1 {
				margin: 0 0 6px;
				font-size: 21px;
				font-weight: 700;
				line-height: 1.05;
				color: ${CLIENT_DOCUMENT_BLUE};
				letter-spacing: 0;
			}

			.assignment-subtitle {
				margin: 0 0 28px;
				font-size: 13px;
				font-weight: 400;
				color: ${CLIENT_DOCUMENT_BODY_COLOR};
			}

			.assignment-section {
				margin: 0 0 18px;
				break-inside: avoid;
				page-break-inside: avoid;
			}

			.assignment-section-kicker {
				margin: 0 0 4px;
				color: #6b7280;
				font-size: 10px;
				font-weight: 600;
				letter-spacing: 0.08em;
				text-transform: uppercase;
			}

			.assignment-document h2 {
				margin: 0 0 10px;
				font-size: 16px;
				font-weight: 600;
				line-height: 1.2;
				color: ${CLIENT_DOCUMENT_BLUE};
			}

			.assignment-scope-section h2:not(:first-of-type) {
				margin-top: 18px;
			}

			.assignment-document h3 {
				margin: 8px 0 5px;
				font-size: 12.5px;
				font-weight: 600;
				line-height: 1.2;
				color: ${CLIENT_DOCUMENT_BLUE};
			}

			.assignment-document p {
				margin: 0 0 9px;
			}

			.assignment-document ul,
			.assignment-document ol {
				margin: 0 0 8px 18px;
				padding: 0;
			}

			.assignment-document li {
				margin: 0 0 4px;
				padding-left: 2px;
			}

			.assignment-compact-list {
				columns: 2;
				column-gap: 34px;
			}

			.assignment-compact-list li {
				break-inside: avoid;
			}

			.assignment-details-table {
				width: 100%;
				border-collapse: collapse;
				border-spacing: 0;
				border-top: 0;
				border-bottom: 0;
				table-layout: fixed;
			}

			.assignment-details-table th,
			.assignment-details-table td {
				border: 0;
				padding: 7px 0 8px;
				vertical-align: top;
			}

			.assignment-details-table tr:not(:last-child) > * {
				border-bottom: 1px solid ${CLIENT_DOCUMENT_BORDER};
			}

			.assignment-details-table th {
				width: 48%;
				padding-right: 14px;
				color: ${CLIENT_DOCUMENT_LABEL_COLOR};
				text-align: left;
				font-weight: 600;
			}

			.assignment-details-table .assignment-schedule-row--bold > * {
				font-weight: 700;
			}

			.assignment-details-table .assignment-schedule-row--italic > * {
				font-style: italic;
			}

			.assignment-details-table .assignment-schedule-row--blue > * {
				color: ${CLIENT_DOCUMENT_BLUE};
			}

			.assignment-details-table td strong,
			.assignment-signature-box strong {
				font-weight: 600;
			}

			.assignment-info-table {
				margin-bottom: 20px;
			}

			.assignment-info-table th {
				width: 200px;
			}

			.assignment-grid-section {
				display: grid;
				grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
				gap: 24px;
				align-items: start;
			}

			.assignment-notes {
				margin-top: 8px;
				font-style: italic;
			}

			.assignment-footnote {
				font-size: 10.5px;
				font-style: italic;
			}

			.assignment-signatures {
				display: grid;
				grid-template-columns: 1fr 1fr;
				gap: 44px;
				margin-top: 22px;
			}

			.assignment-signature-box {
				display: flex;
				flex-direction: column;
				min-height: 154px;
				gap: 7px;
			}

			.assignment-signature-box h3,
			.assignment-signature-box strong,
			.assignment-signature-box span {
				margin: 0;
			}

			.assignment-signature-line,
			.assignment-date-line {
				border-bottom: 1px solid #333;
				height: 26px;
			}

			.assignment-signature-line {
				margin-top: auto;
			}

			.assignment-page-footer {
				position: absolute;
				left: 0.74in;
				right: 0.74in;
				bottom: 0.32in;
				padding-top: 10px;
				border-top: 1px solid #e5e5e5;
				font-size: 10px;
				line-height: 1.25;
				color: #555;
				white-space: pre-wrap;
			}

			.assignment-page-number {
				display: block;
				margin-top: 4px;
				color: #777;
				font-size: 9px;
				text-align: right;
			}

			/* From page 2: the document's name above a thin line, as on the director's Word documents. */
			.assignment-page-header {
				margin: 0 0 18px;
				padding-bottom: 5px;
				border-bottom: 1px solid #e5e5e5;
				font-size: 10px;
				line-height: 1.25;
				color: #555;
				text-align: right;
			}

			@media print {
				.assignment-pdf-root {
					padding: 0;
					background: #fff;
				}

				.assignment-document {
					margin: 0;
					box-shadow: none;
				}
			}
		</style>
	`;
};
